/* eslint-disable react-hooks/exhaustive-deps */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getUserInfo } from "@/lib/token";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import { useEffect, useMemo, useState } from "react";
import { companies } from "@/services/global.services";
import { useToast } from "@/hooks/use-toast";
import { GenericType } from "@/types";
import { getJobId } from "@/lib/utils";

export const useMutations = () => {
  const API_SERVICE = useMemo(() => {
    return companies;
  }, [companies]);

  const { toast } = useToast();

  const { user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);

  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [companyList, setCompanyList] = useState<GenericType>([]);
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [activeRecordData, setActiveRecordData] =
    useState<GenericType>(undefined);
  const [tableColumn, setTableColumn] = useState([]);
  const [deleteId, setDeleteId] = useState(undefined);

  const { setSearchProgress, search } = useGlobalSearch();
  const [searchDebounced] = useDebouncedValue(search, 200);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();
  const [filter, setFilter] = useState({});

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  //On Load Component
  useEffect(() => {
    (async () => {
      await loadingUsers({ searchText: "" });
      await loadingSncCompany();
      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
  }, []);

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse = await deleteRecord();
        if (deleteResponse.data) {
          if (deleteResponse.data?.job_id) {
            const jobIdResponse = await getJobId(deleteResponse.data?.job_id);
            if (jobIdResponse.data) {
              await loadingUsers({});

              toast({
                title: "Success",
                variant: "success",
                description: jobIdResponse.data.result.metadata.message,
              });
            }
          }
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {},
      company_id: deleteId,
    };
    console.log(API_SERVICE.delete);
    const { data, error, message } = await apiServices<GenericType>(
      API_SERVICE.delete,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        await loadingUsers({});
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingUsers({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingUsers({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: GenericType = [];
  const onAction = async ({ action, data }: GenericType) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data.company_id);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data.user_id,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this company?",
      });
      await loadingUsers({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);
    }
  }, [formPopupOpen]);

  const transformArray = (arr: string[]) => {
    return arr.map((item) => ({ value: item, label: item }));
  };

  const loadingSncCompany = async () => {
    const payload = {
      data: {
        filter_by: {
          search_text: "",
          exact_match: false,
        },
      },
      company_id: "",
      user_id: "",
    };
    const { data, error, message } = await apiServices<GenericType>(
      API_SERVICE.company,
      "post",
      payload,
    );

    if (data?.result?.metadata?.company) {
      setCompanyList(transformArray(data.result.metadata.company));
      // setListData(data.result.metadata[API_SERVICE.key]);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };
  const loadingUsers = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
  }) => {
    let apiFilter = {};

    if (!filterReset) {
      apiFilter = filter;
    }

    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const payload = {
      data: {
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: "cts",
            order_by: false,
          },
        },
      },
      company_id: "",
      user_id: "",
    };
    const { data, error, message } = await apiServices<GenericType>(
      API_SERVICE.get,
      "post",
      payload,
    );

    if (data) {
      setListData(data.result.metadata[API_SERVICE.key]);
      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  const onFormSubmit = async (prop: GenericType): Promise<GenericType> => {
    let payloadData = {};
    // Keep existing company settings so only these two keys change
    const settings = {
      ...(activeRecordData?.settings || {}),
      country: prop.country,
      show_deleted_documents: prop.show_deleted_documents ?? false,
    };
    if (activeRecordData?.company_id) {
      payloadData = {
        company_id: prop.company_id,
        display_name: prop.display_name,
        snc_company_id: prop.snc_company_id,
        settings,
      };
    } else {
      payloadData = {
        company_id: prop.company_id,
        display_name: prop.display_name,
        snc_company_id: prop.snc_company_id,
        settings,
        is_new: true,
      };
    }
    const payload = {
      data: payloadData,
      company_id: prop.company_id,
      user_id: user_id,
    };

    const { data, error } = await apiServices<GenericType>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data?.job_id) {
      const jobIdResponse = await getJobId(data.job_id);
      if (jobIdResponse.error) {
        return jobIdResponse;
      }
      const getResponse = await loadingUsers({});
      if (getResponse.error) {
        return getResponse;
      }
      if (getResponse.data) {
        toast({
          title: "Success",
          variant: "success",
          description:
            jobIdResponse?.data?.result?.metadata?.message ??
            jobIdResponse?.data?.status?.msg ??
            jobIdResponse?.data?.status?.message,
        });
      }
    }

    if (error) {
      setProcess(false);
    }
    return { data, error };
  };

  const filterRequest = async () => {
    setProcess(true);
    await loadingUsers({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingUsers({ filterReset: true });
    setProcess(false);
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    tableColumn,
    setTableColumn,
    companyList,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
  };
};
